import { useEffect, useRef, useCallback } from 'react';
import { RefreshShortcutContext } from './RefreshShortcutContext';

export function RefreshShortcutProvider({ children }: { children: React.ReactNode }) {
  const callbacksRef = useRef<Record<string, () => void>>({});
  const scopeStackRef = useRef<string[]>([]);
  const globalOrderRef = useRef<string[]>([]);

  const registerRefresh = useCallback((scope: string, callback: () => void) => {
    callbacksRef.current[scope] = callback;
    if (!scope.startsWith('global-')) {
      return;
    }
    globalOrderRef.current = globalOrderRef.current.filter((id) => id !== scope);
    globalOrderRef.current.push(scope);
  }, []);

  const unregisterRefresh = useCallback((scope: string) => {
    delete callbacksRef.current[scope];
    globalOrderRef.current = globalOrderRef.current.filter((id) => id !== scope);
  }, []);

  const setActiveScope = useCallback((scope: string | null) => {
    if (scope === null) {
      scopeStackRef.current.pop();
    } else {
      scopeStackRef.current.push(scope);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || event.code !== 'KeyR') return;

      const target = event.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isInput) return;

      const activeScope = scopeStackRef.current[scopeStackRef.current.length - 1] || null;
      if (activeScope && callbacksRef.current[activeScope]) {
        event.preventDefault();
        callbacksRef.current[activeScope]();
        return;
      }

      const globals = globalOrderRef.current;
      if (globals.length === 0) return;

      event.preventDefault();
      const lastGlobal = globals[globals.length - 1];
      callbacksRef.current[lastGlobal]?.();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  return (
    <RefreshShortcutContext.Provider value={{ registerRefresh, unregisterRefresh, setActiveScope }}>
      {children}
    </RefreshShortcutContext.Provider>
  );
}
